import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  timeout: 300000, // 5 minutes for large file processing
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to prevent infinite refresh loops
let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

const onTokenRefreshed = (token) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

// Request interceptor - add auth header
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle 401 and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't retry if it's the login or refresh endpoint
      if (originalRequest.url?.includes('/auth/login') || originalRequest.url?.includes('/auth/refresh')) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Wait for the refresh to complete
        return new Promise((resolve) => {
          subscribeTokenRefresh((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        // No refresh token, redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {
          refreshToken,
        });

        const { accessToken, refreshToken: newRefreshToken } = response.data.data;

        localStorage.setItem('token', accessToken);
        localStorage.setItem('refreshToken', newRefreshToken);

        isRefreshing = false;
        onTokenRefreshed(accessToken);

        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    const message = error.response?.data?.error?.message || error.response?.data?.error || error.message || 'An error occurred';
    console.error('API Response Error:', message);
    return Promise.reject(error);
  }
);

// ==========================================
// Authentication API
// ==========================================

export const login = async (email, password) => {
  const response = await api.post('/auth/login', { email, password });
  return response.data;
};

export const logout = async () => {
  const refreshToken = localStorage.getItem('refreshToken');
  try {
    await api.post('/auth/logout', { refreshToken });
  } catch {
    // Ignore logout errors
  }
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
};

export const refreshTokenRequest = async () => {
  const refreshToken = localStorage.getItem('refreshToken');
  const response = await api.post('/auth/refresh', { refreshToken });
  return response.data;
};

export const getCurrentUser = async () => {
  const response = await api.get('/auth/me');
  return response.data;
};

export const changePassword = async (currentPassword, newPassword) => {
  const response = await api.post('/auth/change-password', { currentPassword, newPassword });
  return response.data;
};

// ==========================================
// Admin API
// ==========================================

export const getUsers = async () => {
  const response = await api.get('/admin/users');
  return response.data;
};

export const createUser = async (userData) => {
  const response = await api.post('/admin/users', userData);
  return response.data;
};

export const getUser = async (id) => {
  const response = await api.get(`/admin/users/${id}`);
  return response.data;
};

export const updateUser = async (id, userData) => {
  const response = await api.put(`/admin/users/${id}`, userData);
  return response.data;
};

export const deleteUser = async (id) => {
  const response = await api.delete(`/admin/users/${id}`);
  return response.data;
};

export const resetUserPassword = async (id, password) => {
  const response = await api.post(`/admin/users/${id}/reset-password`, { password });
  return response.data;
};

// ==========================================
// Admin Settings API
// ==========================================

export const getSettings = async () => {
  return await api.get('/admin/settings');
};

export const updateSettings = async (data) => {
  return await api.put('/admin/settings', data);
};

export const testApiKey = async (apiKey) => {
  return await api.post('/admin/settings/test-key', { apiKey });
};

// ==========================================
// Helper to get auth header for fetch requests (SSE)
// ==========================================

export const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Health check
export const checkHealth = async () => {
  const response = await api.get('/health');
  return response.data;
};

// Get available models
export const getModels = async () => {
  const response = await api.get('/config/models');
  return response.data;
};

// File upload
export const uploadFiles = async (files, onProgress) => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files', file);
  });

  const response = await api.post('/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percent);
      }
    },
  });

  return response.data;
};

// Delete uploaded file
export const deleteFile = async (filename) => {
  const response = await api.delete(`/upload/${encodeURIComponent(filename)}`);
  return response.data;
};

// Process files with AI (legacy non-streaming)
export const processFiles = async (filenames, options = {}) => {
  const response = await api.post('/process', {
    filenames,
    options: {
      batchMode: options.batchMode ?? true,
      model: options.model, // Optional model override
    },
  });
  return response.data;
};

/**
 * Process files with streaming updates using Server-Sent Events
 * Provides real-time progress for each file including automatic validation reruns
 *
 * @param {string[]} filenames - Array of server filenames to process
 * @param {Object} options - Processing options
 * @param {string} options.model - AI model to use for first pass extraction
 * @param {string} options.verificationModel - AI model to use for second pass verification
 * @param {boolean} options.autoRerun - Enable automatic rerun on validation failure (default: true)
 * @param {Function} onStatus - Callback for status updates: (filename, statusData) => void
 * @param {Function} onComplete - Callback when all processing completes: (results) => void
 * @param {Function} onError - Callback for errors: (error) => void
 * @returns {Function} Abort function to cancel the stream
 */
export const processFilesStreaming = (filenames, options = {}, { onStatus, onComplete, onError, onStart }) => {
  const controller = new AbortController();

  const startStream = async () => {
    try {
      console.log('[SSE Client] Starting stream request...');
      const response = await fetch(`${API_BASE_URL}/api/process/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
        body: JSON.stringify({
          filenames,
          options: {
            model: options.model,
            verificationModel: options.verificationModel,
            autoRerun: options.autoRerun ?? true,
          },
        }),
        signal: controller.signal,
      });

      console.log('[SSE Client] Response received:', response.status, response.headers.get('content-type'));

      if (!response.ok) {
        // For non-SSE error responses, try to parse as JSON
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.error || `HTTP error ${response.status}`);
        }
        throw new Error(`HTTP error ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      console.log('[SSE Client] Starting to read stream...');

      // Helper function to process SSE event blocks
      const processEventBlock = (eventBlock) => {
        if (!eventBlock.trim()) return;

        const lines = eventBlock.split('\n');
        let eventType = null;
        let eventData = null;

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.substring(7).trim();
          } else if (line.startsWith('data: ')) {
            eventData = line.substring(6);
          }
        }

        if (eventType && eventData) {
          try {
            const data = JSON.parse(eventData);
            console.log('[SSE Client] Parsed event:', eventType, data.filename || data.jobId || '');

            switch (eventType) {
              case 'start':
                if (onStart) {
                  onStart(data);
                }
                break;
              case 'status':
                if (onStatus) {
                  onStatus(data.filename, data);
                }
                break;
              case 'complete':
                if (onComplete) {
                  onComplete(data);
                }
                break;
              default:
                console.warn('[SSE Client] Unknown event:', eventType);
            }
          } catch (parseError) {
            console.error('[SSE Client] Failed to parse SSE data:', parseError, eventData);
          }
        }
      };

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          console.log('[SSE Client] Stream ended');
          // Process any remaining data in the buffer when stream ends
          if (buffer.trim()) {
            console.log('[SSE Client] Processing remaining buffer:', buffer.substring(0, 100));
            processEventBlock(buffer);
          }
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        console.log('[SSE Client] Received chunk:', chunk.substring(0, 100));
        buffer += chunk;

        // SSE events are separated by double newlines
        const events = buffer.split('\n\n');
        // Keep the last incomplete event in the buffer
        buffer = events.pop() || '';

        console.log('[SSE Client] Found', events.length, 'complete events');

        for (const eventBlock of events) {
          processEventBlock(eventBlock);
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Stream aborted by user');
        return;
      }
      console.error('Stream error:', error);
      if (onError) {
        onError(error);
      }
    }
  };

  startStream();

  // Return abort function
  return () => {
    controller.abort();
  };
};

// Get processing job status
export const getJobStatus = async (jobId) => {
  const response = await api.get(`/process/${jobId}`);
  return response.data;
};

// Validate transactions
export const validateTransactions = async (metadata, transactions) => {
  const response = await api.post('/validate', {
    metadata,
    transactions,
  });
  return response.data;
};

// Quick balance check
export const quickBalanceCheck = async (metadata, transactions) => {
  const response = await api.post('/validate/quick', {
    metadata,
    transactions,
  });
  return response.data;
};

// Validate with balance override
export const validateWithOverride = async (metadata, transactions, balanceOverride) => {
  const response = await api.post('/validate/override', {
    metadata,
    transactions,
    balanceOverride,
  });
  return response.data;
};

// Challenge rerun - re-extract with error context using specified model
export const challengeRerun = async (filename, validationErrors, options = {}) => {
  const response = await api.post(`/process/challenge/${encodeURIComponent(filename)}`, {
    errors: validationErrors,
    options: {
      model: options.model || 'openai/gpt-5-mini',
    },
  });
  return response.data;
};

// Export to CSV or XLSX
export const exportFiles = async (statements, options = {}) => {
  const response = await api.post('/export', {
    statements,
    mode: options.mode || 'merged',
    format: options.format || 'csv',
    mergedFilename: options.mergedFilename,
  });
  return response.data;
};

// Legacy alias for backward compatibility
export const exportCSV = exportFiles;

// Get export info
export const getExportInfo = async (exportId) => {
  const response = await api.get(`/export/${exportId}/info`);
  return response.data;
};

// Download export file
// expectedFilename: pass the filename we already know from export result to ensure correct extension
// fileIndex: optional index to download a specific file (for individual exports)
export const downloadExport = async (exportId, expectedFilename = null, fileIndex = null) => {
  const url = fileIndex !== null
    ? `/export/${exportId}/${fileIndex}`
    : `/export/${exportId}`;

  const response = await api.get(url, {
    responseType: 'blob',
  });

  // Use expected filename if provided, otherwise try to extract from header
  let filename = expectedFilename || 'qbo_export.csv';
  if (!expectedFilename) {
    const contentDisposition = response.headers['content-disposition'];
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
      if (match) {
        filename = match[1];
      }
    }
  }

  // Determine MIME type based on filename extension
  let mimeType = 'text/csv';
  if (filename.endsWith('.xlsx')) {
    mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  }

  // Create download link with proper MIME type
  const blob = new Blob([response.data], { type: mimeType });
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);

  return { success: true, filename };
};

// Download all export files one by one
export const downloadAllExports = async (exportId, files) => {
  const results = [];
  for (let i = 0; i < files.length; i++) {
    // Small delay between downloads to prevent browser blocking
    if (i > 0) {
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    const result = await downloadExport(exportId, files[i].filename, i);
    results.push(result);
  }
  return results;
};

export default api;
